/**
 * The "Overrides" section of the sliding job launch drawer.
 * Let the user specify overrides (args, config, entry point) for
 * what we are launching.
 */

import Popover from '@material-ui/core/Popover';
import {Button} from '@wandb/weave/components/Button';
import React, {useState} from 'react';

import {CodeEditor} from '../CodeEditor';
import {JobRun, JobRuns} from '../JobDetail/utilTypes';
import * as S from './JobDrawerOverrides.styles';
import {SelectRunPanel} from './SelectRunPanel';

type JobDrawerOverridesProps = {
  previousRuns: JobRuns;
  cloneRun: JobRun | null;
  lockedRunToClone?: boolean;
  overrides: string;
  onOverridesChange: (value: string) => void;
  onSelectRun: (run: JobRun | null) => void;
  customRunColors: Record<string, string>;
};

export const JobDrawerOverrides = ({
  previousRuns,
  cloneRun,
  lockedRunToClone,
  overrides,
  onOverridesChange,
  onSelectRun,
  customRunColors,
}: JobDrawerOverridesProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onCloneFromClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const onPopoverClose = () => {
    setAnchorEl(null);
  };

  // When user manually edits overrides, clear the selected run to clone
  const onChange = (value: string) => {
    onSelectRun(null);
    onOverridesChange(value);
  };

  return (
    <>
      <S.LabelRow>
        <S.Label>Overrides</S.Label>
        {previousRuns.length > 0 && (
          <>
            <S.PasteFrom>
              <S.Label>Clone from</S.Label>
              {lockedRunToClone ? (
                <S.Label>{cloneRun ? cloneRun.displayName : 'None'}</S.Label>
              ) : (
                <Button variant="ghost" onClick={onCloneFromClick}>
                  {cloneRun ? cloneRun.displayName : 'None'}
                </Button>
              )}
            </S.PasteFrom>
            {!lockedRunToClone && (
              <Popover
                id="job-drawer-paste-from-popover"
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={onPopoverClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}>
                <SelectRunPanel
                  runs={previousRuns}
                  customRunColors={customRunColors}
                  onSelectRun={onSelectRun}
                  onClose={onPopoverClose}
                  locked={lockedRunToClone}
                />
              </Popover>
            )}
          </>
        )}
      </S.LabelRow>
      <CodeEditor value={overrides} language="json" onChange={onChange} />
    </>
  );
};
